import useSWRImmutable from 'swr/immutable';

type Icons8HealthCheckResponse = {
  success: boolean;
  monitors: {
    friendlyName: string;
    status: string;
  }[];
};

const ICONS8_MONITOR_FRIENDLYNAME = 'Icons API';
const ICONS8_VECTOR_MONITOR_FRIENDLYNAME = 'Vectors API';

/**
 * Check if the service at `url` is available
 *
 * @param url
 * @returns Promise<boolean> resolves to TRUE if the service is available, FALSE otherwise
 */
const checkServiceAvailability = async (url: string, maxResponseTime?: number): Promise<boolean> => {
  // Wait max 20s for response by default
  const DEFAULT_MAX_RESPONSE_TIME = 20000;

  try {
    const controller = new AbortController();
    const { signal } = controller;
    const id = setTimeout(() => controller.abort(), maxResponseTime || DEFAULT_MAX_RESPONSE_TIME);
    const response = await fetch(url, { signal });
    clearTimeout(id);

    if (response.status !== 200) {
      return false;
    }

    const body = (await response.json()) as Icons8HealthCheckResponse;
    if (body.success !== true) {
      return false;
    }

    if (!body.monitors || !body.monitors.length) {
      return false;
    }
    const icons8Monitor = body.monitors.find((monitor) => monitor.friendlyName === ICONS8_MONITOR_FRIENDLYNAME);
    const icons8VectorMonitor = body.monitors.find(
      (monitor) => monitor.friendlyName === ICONS8_VECTOR_MONITOR_FRIENDLYNAME,
    );

    if (!icons8Monitor || !icons8VectorMonitor) {
      return false;
    }
    return icons8Monitor.status === 'OK' && icons8VectorMonitor.status === 'OK';
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('Icons8 ping request timed out');
    }

    return false;
  }
};

/**
 * Hook to check if the Icons8 service is available - performs a single ping request per page load
 * If false, should block all icons8 requests
 *
 * @returns TRUE if the service is available, FALSE otherwise
 */
const ICONS8_SERVICE_URL = 'https://api-icons.icons8.com/publicApi/healthcheck';
export const useIcons8ServiceAvailability = (maxTimeout?: number) => {
  const { data, error } = useSWRImmutable<boolean>('ICONS8_SERVICE_PING', () =>
    checkServiceAvailability(ICONS8_SERVICE_URL, maxTimeout),
  );

  if (!data && !error) return false;
  return data;
};
